import React from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Button,
  Container,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert,
} from '@mui/material'
import {
  ArrowBack,
  Security,
  Lock,
  Shield,
  VerifiedUser,
  CheckCircle,
  Cloud,
  PhoneIphone,
  VpnKey,
} from '@mui/icons-material'

const PrivacySecurity: React.FC = () => {
  const navigate = useNavigate()

  const securityFeatures = [
    {
      icon: <Lock fontSize="large" />,
      title: 'End-to-End Encryption',
      description: 'Your data is encrypted both in transit and at rest',
      features: [
        'TLS/SSL encryption for all communications',
        'AES-256 encryption for stored data',
        'Secure key management',
        'Industry-standard protocols',
      ]
    },
    {
      icon: <Shield fontSize="large" />,
      title: 'HIPAA Compliance',
      description: 'We follow HIPAA guidelines for health information',
      features: [
        'Secure data handling practices',
        'Privacy rule compliance',
        'Security rule implementation',
        'Regular security audits',
      ]
    },
    {
      icon: <VerifiedUser fontSize="large" />,
      title: 'Data Control',
      description: 'You maintain complete control over your information',
      features: [
        'Export your data anytime',
        'Delete your account and data',
        'Control what data you share',
        'Review access logs',
      ]
    },
    {
      icon: <VpnKey fontSize="large" />,
      title: 'Access Control',
      description: 'Secure authentication and authorization',
      features: [
        'Strong password requirements',
        'Secure session management',
        'Account recovery options',
        'Activity monitoring',
      ]
    },
  ]

  const privacyPractices = [
    'We never sell your personal health information',
    'You control what data is shared',
    'Data is encrypted and securely stored',
    'You can export or delete your data anytime',
    'Minimal data collection - only what\'s necessary',
    'Transparent privacy policy',
  ]

  const dataStorage = [
    {
      icon: <PhoneIphone />,
      title: 'iOS App - On-Device',
      description: 'Your health data is primarily stored securely on your device',
    },
    {
      icon: <Cloud />,
      title: 'Web App - Encrypted Cloud',
      description: 'Web data is stored on encrypted servers with strict access controls',
    },
  ]

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', py: 4 }}>
      <Container maxWidth="lg">
        <Button 
          startIcon={<ArrowBack />}
          onClick={() => navigate('/')}
          sx={{ mb: 3 }}
        >
          Back to Home
        </Button>

        <Paper sx={{ p: 4 }}>
          <Box textAlign="center" mb={6}>
            <Typography variant="h2" fontWeight="bold" gutterBottom>
              Privacy & Security
            </Typography>
            <Typography variant="h5" color="text.secondary">
              Your health data is protected with enterprise-grade security
            </Typography>
          </Box>

          {/* Hero Section */}
          <Box sx={{ bgcolor: 'primary.main', color: 'white', p: 4, borderRadius: 3, mb: 6 }}>
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} md={6}>
                <Typography variant="h4" fontWeight="bold" gutterBottom>
                  Privacy-First Approach
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  We understand the sensitive nature of health information. That's why we've built 
                  TrialFinder with privacy and security at its core. Your data is encrypted, secure, 
                  and always under your control.
                </Typography>
                <Button 
                  variant="contained" 
                  size="large"
                  sx={{ bgcolor: 'white', color: 'primary.main', '&:hover': { bgcolor: 'grey.100' } }}
                  onClick={() => navigate('/privacy')}
                >
                  Read Full Privacy Policy
                </Button>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ bgcolor: 'rgba(255,255,255,0.1)', p: 3, borderRadius: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    Our Privacy Commitment
                  </Typography>
                  {privacyPractices.map((practice, index) => (
                    <Box key={index} display="flex" alignItems="center" gap={1} mb={1.5}>
                      <CheckCircle />
                      <Typography variant="body2">{practice}</Typography>
                    </Box>
                  ))}
                </Box>
              </Grid>
            </Grid>
          </Box>

          {/* Important Warning */}
          <Alert severity="warning" sx={{ mb: 6 }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Privacy Protection Tip
            </Typography>
            <Typography variant="body2">
              When entering health information or taking photos of medical documents, please <strong>EXCLUDE 
              or MASK</strong> personal identifiers such as your full name, address, Social Security Number, 
              date of birth, and medical record numbers. Only include medical information relevant to 
              searching for clinical trials.
            </Typography>
          </Alert>

          {/* Security Features */}
          <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ mt: 6, mb: 3 }}>
            Enterprise-Grade Security
          </Typography>
          <Grid container spacing={3} sx={{ mb: 6 }}>
            {securityFeatures.map((feature, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Card sx={{ height: '100%', '&:hover': { boxShadow: 6 } }}>
                  <CardContent>
                    <Box display="flex" alignItems="center" gap={2} mb={2}>
                      <Box sx={{ color: 'primary.main' }}>
                        {feature.icon}
                      </Box>
                      <Typography variant="h5" fontWeight="bold">
                        {feature.title}
                      </Typography>
                    </Box>
                    <Typography variant="body1" color="text.secondary" paragraph>
                      {feature.description}
                    </Typography>
                    <List dense>
                      {feature.features.map((item, idx) => (
                        <ListItem key={idx}>
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <CheckCircle color="success" fontSize="small" />
                          </ListItemIcon>
                          <ListItemText primary={item} />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Data Storage */}
          <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ mt: 6, mb: 3 }}>
            Where Your Data Lives
          </Typography>
          <Grid container spacing={3} sx={{ mb: 6 }}>
            {dataStorage.map((storage, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Card sx={{ textAlign: 'center', p: 3, height: '100%' }}>
                  <Box sx={{ color: 'primary.main', mb: 2 }}>
                    {React.cloneElement(storage.icon, { sx: { fontSize: 64 } })}
                  </Box>
                  <Typography variant="h5" fontWeight="bold" gutterBottom>
                    {storage.title}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    {storage.description}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Third-Party Services */}
          <Box sx={{ bgcolor: 'grey.50', p: 4, borderRadius: 3, mb: 6 }}>
            <Typography variant="h5" fontWeight="bold" gutterBottom>
              Third-Party Services
            </Typography>
            <Typography variant="body1" paragraph>
              We use trusted third-party services to provide our features:
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" fontWeight="bold" gutterBottom>
                      OpenAI API
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      For AI-powered health data analysis. Data is processed according to their 
                      privacy policy and data processing agreements.
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" fontWeight="bold" gutterBottom>
                      ClinicalTrials.gov
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      For clinical trial search. No personal information is sent to this public database.
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" fontWeight="bold" gutterBottom>
                      Payment Processors
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      For secure payment processing. No payment card data is stored on our servers.
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>

          {/* Your Rights */}
          <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ mt: 6, mb: 3 }}>
            Your Privacy Rights
          </Typography>
          <Grid container spacing={3} sx={{ mb: 6 }}>
            <Grid item xs={12} md={6}>
              <List>
                <ListItem>
                  <ListItemIcon>
                    <Security color="primary" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Access Your Data"
                    secondary="Request a copy of all your personal data"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Security color="primary" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Correct Information"
                    secondary="Update or correct inaccurate information"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Security color="primary" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Delete Your Account"
                    secondary="Request deletion of your account and data"
                  />
                </ListItem>
              </List>
            </Grid>
            <Grid item xs={12} md={6}>
              <List>
                <ListItem>
                  <ListItemIcon>
                    <Security color="primary" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Export Data"
                    secondary="Download your health data in portable format"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Security color="primary" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Opt-Out"
                    secondary="Unsubscribe from marketing communications"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Security color="primary" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Restrict Processing"
                    secondary="Limit how we use your information"
                  />
                </ListItem>
              </List>
            </Grid>
          </Grid>

          {/* Contact */}
          <Box sx={{ bgcolor: 'primary.lighter', p: 4, borderRadius: 3, textAlign: 'center' }}>
            <Typography variant="h5" fontWeight="bold" gutterBottom>
              Questions About Privacy or Security?
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              We're here to help. Contact us with any concerns or questions.
            </Typography>
            <Box display="flex" gap={2} justifyContent="center" flexWrap="wrap">
              <Button 
                variant="contained"
                onClick={() => navigate('/privacy')}
              >
                Privacy Policy
              </Button>
              <Button 
                variant="outlined"
                onClick={() => window.location.href = 'mailto:info@bluebithealth.com'}
              >
                Email Us
              </Button>
            </Box>
          </Box>

          {/* CTA */}
          <Box textAlign="center" sx={{ mt: 6 }}>
            <Typography variant="h5" fontWeight="bold" gutterBottom>
              Your Data is Safe With Us
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Start using TrialFinder with confidence
            </Typography>
            <Button 
              variant="contained" 
              size="large"
              onClick={() => navigate('/register')}
              sx={{ px: 6, py: 2 }}
            >
              Get Started Free
            </Button>
          </Box>
        </Paper>
      </Container>
    </Box>
  )
}

export default PrivacySecurity

